import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/lib/auth';
import { I18nProvider } from '@/lib/i18n';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/lib/theme';
import ProtectedRoute from '@/components/ProtectedRoute';
import PublicLayout from '@/components/layout/PublicLayout';
import AdminLayout from '@/components/layout/AdminLayout';
import TraineeLayout from '@/components/layout/TraineeLayout';

import PublicHome from '@/pages/PublicHome';
import Login from '@/pages/Login';
import SsoLanding from '@/pages/SsoLanding';
import MoviePage from '@/pages/MoviePage';

import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminLearnerDetail from '@/pages/admin/AdminLearnerDetail';
import AdminGameBreakdown from '@/pages/admin/AdminGameBreakdown';
import AdminContest from '@/pages/admin/AdminContest';
import AdminAIAnalytics from '@/pages/admin/AdminAIAnalytics';
import AdminDocs from '@/pages/admin/AdminDocs';
import AdminQuiz from '@/pages/admin/AdminQuiz';
import AdminCertificates from '@/pages/admin/AdminCertificates';
import AdminGames from '@/pages/admin/AdminGames';
import AdminBadges from '@/pages/admin/AdminBadges';
import AdminOfflineTraining from '@/pages/admin/AdminOfflineTraining';
import AdminTaxonomies from '@/pages/admin/AdminTaxonomies';
import AdminBrand from '@/pages/admin/AdminBrand';
import AdminReports from '@/pages/admin/AdminReports';
import AdminVideos from '@/pages/admin/AdminVideos';
import AdminStories from '@/pages/admin/AdminStories';
import AdminPathways from '@/pages/admin/AdminPathways';
import AdminNotifications from '@/pages/admin/AdminNotifications';
import AdminCohorts from '@/pages/admin/AdminCohorts';
import AdminArcadeQuestions from '@/pages/admin/AdminArcadeQuestions';
import AdminLoyaltyCredits from '@/pages/admin/AdminLoyaltyCredits';
import AdminUAT from '@/pages/admin/AdminUAT';
import AdminCallCenter from '@/pages/admin/AdminCallCenter';
import CallCenterLookup from '@/pages/CallCenterLookup';

import TraineeDashboard from '@/pages/trainee/TraineeDashboard';
import TraineeResume from '@/pages/trainee/TraineeResume';
import TraineeCourses from '@/pages/trainee/TraineeCourses';
import TraineeCourseDetail from '@/pages/trainee/TraineeCourseDetail';
import TraineePathways from '@/pages/trainee/TraineePathways';
import TraineeBadges from '@/pages/trainee/TraineeBadges';
import TraineeCertificates from '@/pages/trainee/TraineeCertificates';
import TraineeQuiz from '@/pages/trainee/TraineeQuiz';
import Arcade from '@/pages/trainee/Arcade';
import GameRoute from '@/pages/trainee/GameRoute';
import Leaderboard from '@/pages/trainee/Leaderboard';
import Profile from '@/pages/trainee/Profile';

export default function App() {
    return (
        <BrowserRouter>
            <Toaster richColors position="top-center" />
            <ThemeProvider>
            <AuthProvider>
                <I18nProvider>
                <Routes>
                    {/* Public */}
                    <Route element={<PublicLayout />}>
                        <Route path="/" element={<PublicHome />} />
                    </Route>
                    <Route path="/login" element={<Login />} />
                    <Route path="/sso" element={<SsoLanding />} />
                    <Route path="/movie" element={<MoviePage />} />

                    {/* Trainee */}
                    <Route
                        path="/learn"
                        element={<ProtectedRoute><TraineeLayout /></ProtectedRoute>}
                    >
                        <Route index element={<TraineeDashboard />} />
                        <Route path="reels" element={<Navigate to="/learn/courses" replace />} />
                        <Route path="resume" element={<TraineeResume />} />
                        <Route path="path" element={<TraineePathways />} />
                        <Route path="badges" element={<TraineeBadges />} />
                        <Route path="certificates" element={<TraineeCertificates />} />
                        <Route path="courses" element={<TraineeCourses />} />
                        <Route path="courses/:courseId" element={<TraineeCourseDetail />} />
                        <Route path="quiz/:courseId" element={<TraineeQuiz />} />
                        <Route path="arcade" element={<Arcade />} />
                        <Route path="arcade/:code" element={<GameRoute />} />
                        <Route path="leaderboard" element={<Leaderboard />} />
                        <Route path="profile" element={<Profile />} />
                    </Route>

                    {/* Admin */}
                    <Route
                        path="/admin"
                        element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}
                    >
                        <Route index element={<AdminDashboard />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="learners/:userId" element={<AdminLearnerDetail />} />
                        <Route path="reports/game/:gameCode" element={<AdminGameBreakdown />} />
                        <Route path="contest" element={<AdminContest />} />
                        <Route path="courses" element={<Navigate to="/admin/videos" replace />} />
                        <Route path="ai-creator" element={<Navigate to="/admin/videos" replace />} />
                        <Route path="ai-analytics" element={<AdminAIAnalytics />} />
                        <Route path="certificates" element={<AdminCertificates />} />
                        <Route path="offline-training" element={<AdminOfflineTraining />} />
                        <Route path="games" element={<AdminGames />} />
                        <Route path="rewards" element={<Navigate to="/admin/badges" replace />} />
                        <Route path="badges" element={<AdminBadges />} />
                        <Route path="taxonomies" element={<AdminTaxonomies />} />
                        <Route path="brand" element={<AdminBrand />} />
                        <Route path="reports" element={<AdminReports />} />
                        <Route path="videos" element={<AdminVideos />} />
                        <Route path="stories" element={<AdminStories />} />
                        <Route path="pathways" element={<AdminPathways />} />
                        <Route path="notifications" element={<AdminNotifications />} />
                        <Route path="cohorts" element={<AdminCohorts />} />
                        <Route path="docs" element={<AdminDocs />} />
                        <Route path="quiz" element={<AdminQuiz />} />
                        <Route path="arcade-questions" element={<AdminArcadeQuestions />} />
                        <Route path="loyalty-monitor" element={<AdminLoyaltyCredits />} />
                        <Route path="callcenter" element={<AdminCallCenter />} />
                        <Route path="uat" element={<AdminUAT />} />
                    </Route>

                    {/* Call Centre (view-only support console) */}
                    <Route
                        path="/support"
                        element={<ProtectedRoute requireCallCenter><CallCenterLookup /></ProtectedRoute>}
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </I18nProvider>
            </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}
